import React, { useLayoutEffect, useMemo } from 'react';
import { useLocalSearchParams, useNavigation } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { IllusDressing_1, ModalCloseBtn, theme, useResponsive } from '@sg/garnish';

import { LoadingAnimation, SignedOutView, TransparentModal } from '../../components';
import { useHeaderRight } from '../../components';
import { useIsLoggedIn } from '../../context/AuthMachine';
import { useDerivedRateOrderStatus } from './hooks';
import { useRateOrderNavigation } from './RateOrder.navigation';
import { useRateOrder } from './RateOrderScreen.data';
import { LandingContent } from './RateOrderScreen.landing';
import { RatingContent, RatingWithTitle } from './RateOrderScreen.rating';

// ─────────────────────────────────────────────────────────────────────────────

export const RateOrderScreen = () => {
  const { dismissRateOrder } = useRateOrderNavigation();
  const isLoggedIn = useIsLoggedIn();

  const { minWidth, match } = useResponsive();
  const { formatMessage } = useIntl();

  // ─── Navigation Helpers ──────────────────────────────────────────────

  const navigation = useNavigation();
  const setOptions = navigation.setOptions;

  const navigationHeaderRightItems = useMemo<NavigationHeaderRightItems>(
    () => [
      {
        key: 'account.gift-code.header.close-btn',
        icon: 'IconClose',
        accessibilityLabel: formatMessage(messages.rateOrderClose),
        onPress: dismissRateOrder,
      },
    ],
    [dismissRateOrder, formatMessage],
  );
  const navigationHeaderRight = useHeaderRight({
    items: navigationHeaderRightItems,
  });

  useLayoutEffect(() => {
    setOptions({ headerRight: navigationHeaderRight });
  }, [setOptions, navigationHeaderRight]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <TransparentModal size={match(['full', 'medium'])} onClose={dismissRateOrder}>
      {minWidth.isSM ? <ModalCloseBtn onPress={dismissRateOrder} /> : null}

      {isLoggedIn ? <RateOrderScreenSignedInContent /> : <RateOrderScreenSignedOutContent />}
    </TransparentModal>
  );
};

// ─── Components ──────────────────────────────────────────────────────────────

const RateOrderScreenSignedOutContent = () => {
  return <SignedOutView redirectTo="/account/orders" withoutFooter />;
};

const RateOrderScreenSignedInContent = () => {
  const { orderId } = useLocalSearchParams<{ orderId: string }>();

  const { formatMessage } = useIntl();

  const { isFetching, isInvalidOrder, isRatedOrder, isPendingOrder } =
    useDerivedRateOrderStatus(orderId);

  // ─────────────────────────────────────────────────────────────────────

  if (isFetching) {
    return (
      <View style={styles.loadingContainer}>
        <LoadingAnimation />
      </View>
    );
  }

  if (isInvalidOrder) {
    return (
      <LandingContent>
        <LandingContent.Illustration
          source={IllusDressing_1}
          style={styles.notFoundViewIllustration}
        />

        <LandingContent.Title>
          {formatMessage(messages.rateOrderNotFoundOrderViewTitle)}
        </LandingContent.Title>

        <LandingContent.Subtitle>
          {formatMessage(messages.rateOrderNotFoundOrderViewSubtitle)}
        </LandingContent.Subtitle>
      </LandingContent>
    );
  }

  if (isPendingOrder) {
    return (
      <LandingContent>
        <LandingContent.Illustration />

        <LandingContent.Title>
          {formatMessage(messages.rateOrderPendingOrderViewTitle)}
        </LandingContent.Title>

        <LandingContent.Subtitle>
          {formatMessage(messages.rateOrderPendingOrderViewSubtitle)}
        </LandingContent.Subtitle>
      </LandingContent>
    );
  }

  if (isRatedOrder) {
    return (
      <LandingContent>
        <LandingContent.Illustration />

        <LandingContent.Title>
          {formatMessage(messages.rateOrderRatedOrderViewTitle)}
        </LandingContent.Title>

        <LandingContent.Subtitle>
          {formatMessage(messages.rateOrderRatedOrderViewSubtitle)}
        </LandingContent.Subtitle>
      </LandingContent>
    );
  }

  return <RateOrderScreenSignedInContentRating />;
};

const RateOrderScreenSignedInContentRating = () => {
  const {
    loading,
    submitting,
    landingTitle,
    landingSubtitle,
    ratingTitle,
    ratingSubtitle,
    ratingHint,
    ratingCta,
    cards,
    rating,
    handleHoverRating,
    handleRating,
    handleCardSelection,
    handleSubmit,
  } = useRateOrder();

  // ─────────────────────────────────────────────

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <LoadingAnimation />
      </View>
    );
  }

  return (
    <>
      {rating === 0 && landingTitle ? (
        <LandingContent>
          <LandingContent.Illustration />

          <LandingContent.Title>{landingTitle}</LandingContent.Title>

          <LandingContent.Subtitle>{landingSubtitle}</LandingContent.Subtitle>

          <RatingWithTitle
            rating={rating}
            ratingTitle={ratingTitle}
            onRating={handleRating}
            onHoverRating={handleHoverRating}
          />
        </LandingContent>
      ) : null}

      {rating > 0 && ratingSubtitle ? (
        <RatingContent
          ratingTitle={ratingTitle}
          ratingSubtitle={ratingSubtitle}
          ratingHint={ratingHint}
          ratingCta={ratingCta}
          cards={cards}
          rating={rating}
          submitting={submitting}
          onHoverRating={handleHoverRating}
          onRating={handleRating}
          onSelectCard={handleCardSelection}
          onSubmit={handleSubmit}
        />
      ) : null}
    </>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  rateOrderClose: {
    defaultMessage: 'Close',
    description: 'Rate Order | Close',
  },
  rateOrderNotFoundOrderViewTitle: {
    defaultMessage: `We couldn't find this order.`,
    description: 'Rate | Order | Not Found Order View | Title',
  },
  rateOrderNotFoundOrderViewSubtitle: {
    defaultMessage: 'Please select another order to rate.',
    description: 'Rate | Order | Not Found Order View | Subtitle',
  },
  rateOrderPendingOrderViewTitle: {
    defaultMessage: 'This order is still in progress.',
    description: 'Rate | Order | Pending Order View | Title',
  },
  rateOrderPendingOrderViewSubtitle: {
    defaultMessage: 'Come back to rate this order once it has been completed.',
    description: 'Rate | Order | Pending Order View | Subtitle',
  },
  rateOrderRatedOrderViewTitle: {
    defaultMessage: 'You’ve already rated this order.',
    description: 'Rate | Order | Rated Order View | Title',
  },
  rateOrderRatedOrderViewSubtitle: {
    defaultMessage: 'Thank you! Your feedback means a lot.',
    description: 'Rate | Order | Rated Order View | Subtitle',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    paddingTop: theme.spacing['10'],
    paddingBottom: theme.spacing['20'],
  },
  notFoundViewIllustration: {
    width: 200,
    height: 200,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type NavigationHeaderRightItems = Parameters<typeof useHeaderRight>[0]['items'];
